import React, { useMemo, useCallback } from 'react';
import { StyleSheet, View, Platform, useWindowDimensions } from 'react-native';
import { MasonryFlashList } from '@shopify/flash-list';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import FileCard from '@/components/Files/FileCard';
import Text from '@/components/Typography/Text';
import { isImageFile } from '@/constants/utils/files';
import { getSelectableFileId } from '@/constants/utils/file_selection';

const createStyles = (colors, isXS) => StyleSheet.create({
  container: {
    flex: 1,
  },
  gridContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  listContent: {
    paddingBottom: 20,
  },
  itemWrapper: {
    padding: isXS ? 4 : 6,
  },
  footerLoader: {
    paddingVertical: 20,
    alignItems: 'center',
  },
});

const FilesGridItem = React.memo(({ 
  file, 
  onPress, 
  onSelect, 
  isSelected, 
  selectionDisabled,
  columnWidth,
  isXS,
  showFileStatus,
}) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);
  
  return (
    <View style={[styles.itemWrapper, { width: columnWidth }]}>
      <FileCard
        file={file}
        onPress={onPress}
        onSelect={onSelect}
        isSelected={isSelected}
        selectionDisabled={selectionDisabled}
        showStatus={showFileStatus}
      />
    </View>
  );
});

const FilesGrid = ({
  files = [],
  selectedFiles = new Set(),
  onFileClick,
  onFileSelect,
  onLoadMore,
  isLoading = false,
  pagination = {},
  /** When set (e.g. modal width), column math uses this instead of the window — avoids tiny tiles in a narrow dialog. */
  maxContentWidth,
  showFileStatus = true,
  /** Lower = more columns (smaller tiles). Default 200 matches the main Files page. */
  targetCardWidth = 200,
  refreshControl,
  /** When set, overrides default (macOS disables inner scroll when nested in ScrollView). */
  scrollEnabled: scrollEnabledProp,
  /** Cap for multi-select. Pass null for no cap. Defaults to 10. */
  maxSelection = 10,
}) => {
  const { colors } = useTheme();
  const { isXS, isXL } = useResponsiveness();
  const { width: windowWidth } = useWindowDimensions();
  
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);

  const layoutWidth = useMemo(() => {
    if (maxContentWidth != null && maxContentWidth > 0) {
      return maxContentWidth;
    }
    return null;
  }, [maxContentWidth]);

  // Calculate number of columns based on screen width (or constrained modal width)
  const numColumns = useMemo(() => {
    if (isXS) return 2;
    if (layoutWidth == null && isXL) return 4;
    const availableWidth = layoutWidth ?? Math.min(windowWidth, 1000);
    const cols = Math.floor(availableWidth / targetCardWidth);
    return Math.max(2, Math.min(cols, 5));
  }, [isXS, isXL, windowWidth, layoutWidth, targetCardWidth]);

  const gap = isXS ? 8 : 12;
  const horizontalPadding = 0;
  
  const columnWidth = useMemo(() => {
    const maxWidth = layoutWidth ?? Math.min(windowWidth, isXL ? 1000 : 768);
    const totalGaps = (numColumns - 1) * gap;
    const availableWidth = maxWidth - (horizontalPadding * 2) - totalGaps;
    return availableWidth / numColumns;
  }, [windowWidth, numColumns, gap, horizontalPadding, isXL, layoutWidth]);

  const renderItem = useCallback(({ item }) => {
    const fileId = getSelectableFileId(item);
    return (
      <FilesGridItem
        file={item}
        onPress={onFileClick}
        onSelect={onFileSelect}
        isSelected={selectedFiles.has(fileId)}
        selectionDisabled={Number.isFinite(maxSelection) && selectedFiles.size >= maxSelection && !selectedFiles.has(fileId)}
        columnWidth={columnWidth}
        isXS={isXS}
        showFileStatus={showFileStatus}
      />
    );
  }, [onFileClick, onFileSelect, selectedFiles, columnWidth, isXS, showFileStatus, maxSelection]);

  const keyExtractor = useCallback((item) => getSelectableFileId(item) || item.id, []);

  const getItemType = useCallback((item) => {
    // Group items by whether they're images or not for better recycling
    return isImageFile(item.extension) ? 'image' : 'document';
  }, []);

  const overrideItemLayout = useCallback((layout, item) => {
    // All items have same height in grid view (square aspect ratio + info)
    // Image container is square (columnWidth), plus ~60px for info
    layout.size = columnWidth + 60;
  }, [columnWidth]);

  const ListFooterComponent = useCallback(() => {
    if (!pagination?.hasMore) return null;
    return (
      <View style={styles.footerLoader}>
        <Text size="s" color="secondary">
          {isLoading ? 'Loading more...' : 'Scroll for more'}
        </Text>
      </View>
    );
  }, [pagination, isLoading, styles.footerLoader]);

  const listScrollEnabled = scrollEnabledProp ?? Platform.OS !== 'macos';

  return (
    <View style={styles.container}>
      <MasonryFlashList
        data={files}
        extraData={selectedFiles}
        numColumns={numColumns}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        getItemType={getItemType}
        overrideItemLayout={overrideItemLayout}
        estimatedItemSize={columnWidth + 60}
        contentContainerStyle={styles.listContent}
        onEndReached={onLoadMore}
        onEndReachedThreshold={0.5}
        ListFooterComponent={ListFooterComponent}
        scrollEnabled={listScrollEnabled}
        showsVerticalScrollIndicator={false}
        refreshControl={refreshControl}
      />
    </View>
  );
};

export default React.memo(FilesGrid);

